import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SubscribeForm from "@/components/feature/SubscribeForm";
import useSeo, { SITE_NAME, SITE_ORIGIN } from "@/hooks/useSeo";

const principles = [
  {
    icon: "ri-links-line",
    title: "双信源交叉验证",
    desc: "凡是构成结论的关键信息，至少需要两个可独立追溯的来源。只有一个来源时，我们会明确写出「待确认」。",
  },
  {
    icon: "ri-time-line",
    title: "用时间线代替形容词",
    desc: "与其说「震动业内」，不如把哪天、谁、做了什么列清楚。读者自己看得明白，比我们下判断更重要。",
  },
  {
    icon: "ri-shield-check-line",
    title: "未证实不下结论",
    desc: "事件没有权威结论之前，我们倾向于使用泛指描述，避免在事实未明时对具体主体造成不当影响。",
  },
  {
    icon: "ri-refresh-line",
    title: "持续更新而非重复开篇",
    desc: "有新的进展就直接补充进原文，把变化写清楚，而不是换个标题再发一次博取点击。",
  },
];

const milestones = [
  { year: "2023", title: "从一张表格开始", desc: "最初只是几位从业者整理行业传闻的共享文档，用来互相提醒哪些消息值得留意。" },
  { year: "2024", title: "确立核实流程", desc: "把「双信源交叉」写成了固定动作，线索开始有明确的记录方式与可信度分级。" },
  { year: "2025", title: "开放栏目化内容", desc: "按娱乐、商业、互联网等方向拆分栏目，让不同需求的读者能各取所需。" },
  { year: "2026", title: "一线信息持续更新", desc: "建立了线索邮箱与协作信源网络，日常保持每天若干条的更新节奏。" },
];

const voices = [
  {
    name: "产业研究从业者",
    role: "阅读 1 年",
    text: "它不试图替我做判断，而是把可查证的节点列出来。这种克制在同类内容里其实很少见，反而更有参考价值。",
  },
  {
    name: "品牌公关从业者",
    role: "阅读 9 个月",
    text: "出稿速度不算最快，但基本不会被后来的事实推翻。对我们这种要对外沟通的人来说，这点很重要。",
  },
  {
    name: "内容创作者",
    role: "阅读 6 个月",
    text: "栏目划分很清楚，写商业选题的时候会来翻一翻。看得出编辑是懂行的，不是简单搬运。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - 热点内幕内容原则与联系方式",
    description:
      "了解热点内幕的品牌定位、内容原则、发展理念与联系方式。我们以双信源交叉验证整理一手信息与小道消息，坚持未证实不下结论。",
    keywords: "关于热点内幕,热点内幕内容原则,热点内幕联系方式,一手信息,知情人透露",
    path: "/about",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 热点内幕",
        url: `${SITE_ORIGIN}/about`,
        inLanguage: "zh-CN",
        description: "热点内幕的品牌定位、内容原则、发展理念与联系方式。",
        mainEntity: {
          "@type": "Organization",
          name: SITE_NAME,
          url: `${SITE_ORIGIN}/`,
          email: "tips@redian-neimu.example.com",
          description: "热点事件内幕爆料与知情人线索整理平台。",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_ORIGIN}/about` },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main>
        <section className="border-b border-background-200/70 bg-background-100/60">
          <div className="container-editorial py-8 md:py-12">
            <Breadcrumbs items={[{ label: "关于我们" }]} />
            <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-7">
                <h1 className="font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
                  关于热点内幕
                </h1>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  我们做的事情其实很朴素：把散落在各处的内幕线索，按时间线整理成普通人读得懂的版本。
                  热点来得快、去得也快，我们希望在你看到一堆标题之前，先帮你把事情的来龙去脉理清楚。
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/rumors"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                  >
                    <i className="ri-fire-line text-base" />
                    浏览全部爆料
                  </Link>
                  <a
                    href="#contact"
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-mail-line text-base" />
                    联系我们
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: "2023", label: "起步年份" },
                    { value: "6", label: "内容栏目" },
                    { value: "36", label: "协作信源" },
                    { value: "20+", label: "每日更新" },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="rounded-lg border border-background-200/80 bg-background-50 p-4 text-center"
                    >
                      <p className="font-heading text-2xl font-black text-primary-600">{item.value}</p>
                      <p className="mt-1 text-xs text-foreground-500">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16">
          <div className="container-editorial grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <span className="mb-2 inline-flex items-center gap-2 text-xs font-medium tracking-[0.18em] text-primary-600 uppercase">
                <span className="h-px w-6 bg-primary-500" />
                Positioning
              </span>
              <h2 className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-[28px]">
                网站定位
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                热点内幕不是新闻机构，也不是娱乐八卦的搬运站。我们更像一个「线索整理台」：
                把知情人透露的零散信息、公开可查的时间节点与行业常识放在一起，输出一份结构清晰的记录。
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                所以你会看到我们的文章里有很多具体的时间、地点与顺序描述，却很少有夸张的判断句。
                这不是风格选择，而是我们对「可用」这件事的理解。
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {principles.map((item, index) => (
                  <div
                    key={item.title}
                    className="rounded-lg border border-background-200/80 bg-background-100/60 p-5 animate-fade-up"
                    style={{ animationDelay: `${index * 70}ms` }}
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                    <h3 className="mt-3.5 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100/70 py-14 md:py-16">
          <div className="container-editorial">
            <span className="mb-2 inline-flex items-center gap-2 text-xs font-medium tracking-[0.18em] text-primary-600 uppercase">
              <span className="h-px w-6 bg-primary-500" />
              Our Journey
            </span>
            <h2 className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-[28px]">
              发展理念与历程
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
              从一份共享文档到今天，变的是规模，没变的是那条原则：宁可不写，也不要写出一个经不起回头看的结论。
            </p>

            <div className="mt-9 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {milestones.map((item, index) => (
                <div
                  key={item.year}
                  className="relative rounded-lg border border-background-200/80 bg-background-50 p-5 animate-fade-up"
                  style={{ animationDelay: `${index * 70}ms` }}
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-500 font-heading text-xs font-bold text-background-50">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-3.5 font-heading text-lg font-black text-primary-600">{item.year}</p>
                  <h3 className="mt-1 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-16">
          <div className="container-editorial">
            <span className="mb-2 inline-flex items-center gap-2 text-xs font-medium tracking-[0.18em] text-primary-600 uppercase">
              <span className="h-px w-6 bg-primary-500" />
              Reader Voices
            </span>
            <h2 className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-[28px]">
              用户口碑
            </h2>
            <div className="mt-7 grid grid-cols-1 gap-4 md:grid-cols-3">
              {voices.map((voice, index) => (
                <figure
                  key={voice.name}
                  className="flex h-full flex-col rounded-lg border border-background-200/80 bg-background-100/60 p-5 animate-fade-up"
                  style={{ animationDelay: `${index * 80}ms` }}
                >
                  <i className="ri-double-quotes-l text-2xl text-accent-500" />
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                    {voice.text}
                  </blockquote>
                  <figcaption className="mt-4 border-t border-background-200 pt-3">
                    <span className="block text-sm font-medium text-foreground-950">{voice.name}</span>
                    <span className="mt-0.5 block text-xs text-foreground-500">{voice.role}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="bg-primary-950 py-14 md:py-16">
          <div className="container-editorial grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 rounded-full border border-accent-500/40 bg-accent-500/10 px-3.5 py-1.5 text-xs font-medium text-accent-300">
                <i className="ri-mail-send-line text-sm" />
                Contact
              </span>
              <h2 className="mt-5 font-heading text-2xl font-black tracking-tight text-background-50 md:text-3xl">
                联系我们
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-background-100/70 md:text-base">
                无论你是想提供线索，还是对内容有疑问，都欢迎来信。提供线索时建议附上时间、地点与可核实的细节，我们会严格保密。
              </p>

              <div className="mt-7 space-y-3">
                <div className="flex items-start gap-3 rounded-lg border border-background-50/15 bg-background-50/5 p-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-600 text-background-50">
                    <i className="ri-user-voice-line text-lg" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-background-50">线索提供</p>
                    <p className="mt-0.5 text-sm text-background-100/65">tips@redian-neimu.example.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-background-50/15 bg-background-50/5 p-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
                    <i className="ri-briefcase-line text-lg" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-background-50">内容合作</p>
                    <p className="mt-0.5 text-sm text-background-100/65">hello@redian-neimu.example.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-lg border border-background-50/15 bg-background-50/5 p-4">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                    <i className="ri-time-line text-lg" />
                  </span>
                  <div>
                    <p className="text-sm font-medium text-background-50">回复时间</p>
                    <p className="mt-0.5 text-sm text-background-100/65">工作日 10:00 - 19:00，节假日顺延</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-lg border border-background-50/15 bg-background-50/5 p-5 md:p-6">
                <h3 className="font-heading text-lg font-bold text-background-50">订阅爆料更新</h3>
                <p className="mt-2 text-sm leading-relaxed text-background-100/65">
                  也可以先订阅，有新料时我们会主动通知你。
                </p>
                <div className="mt-5">
                  <SubscribeForm />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}